import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, FileSpreadsheet, Database, Store } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge, ReasonChips } from "@/components/recon/StatusBadge";
import { ReservaBadge } from "@/components/recon/ReservaBadge";
import { ProductThumb, StockMini, money } from "@/components/recon/ProductStock";
import { dlvyStatusLabel } from "@/lib/labels";
import { useCatalogs } from "@/hooks/useCatalogs";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

function CompareRow({ label, s, u, diff }) {
  const cls = (v) =>
    `font-mono text-sm px-2 py-1.5 rounded-sm ${diff ? "bg-[#FEE2E2] text-[#991B1B] font-semibold" : "text-slate-700"}`;
  return (
    <div className="grid grid-cols-2 gap-2 items-center">
      <div className="col-span-2 text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-400">{label}</div>
      <div className={cls(diff)}>{s ?? "—"}</div>
      <div className={cls(diff)}>{u ?? "—"}</div>
    </div>
  );
}

export function OrderDetailDrawer({ result, open, onOpenChange, runId }) {
  const qc = useQueryClient();
  const { routeName, locationName } = useCatalogs();
  const reviewMut = useMutation({
    mutationFn: (id) => api.patch(`/recon/results/${id}/review`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["results", runId] });
      toast.success("Estado de revisión actualizado");
    },
  });

  const r = result;
  const s = r?.storis || {};
  const u = r?.ui || {};
  const reasons = r?.mismatch_reasons || [];
  const has = (x) => reasons.includes(x);
  const routeLbl = (c) => (c ? (routeName(c) ? `${c} · ${routeName(c)}` : c) : null);
  const locLbl = (c) => (c ? (locationName(c) ? `${c} · ${locationName(c)}` : c) : null);
  const rName = routeName(r?.route);
  const oName = locationName(r?.ship_from_location);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto thin-scroll p-0" data-testid="order-detail-drawer">
        {r && (
          <div>
            <SheetHeader className="px-5 pt-5 pb-4 border-b border-[#E2E8F0] space-y-3">
              <div className="flex items-center justify-between gap-3">
                <SheetTitle className="font-heading text-xl font-bold tracking-tight">
                  Orden <span className="font-mono">{r.order_number || "—"}</span>
                </SheetTitle>
                <StatusBadge code={r.match_status} size="md" />
              </div>
              <SheetDescription className="flex items-center gap-2 text-slate-500">
                <span className="font-mono text-slate-700">{r.model_number || "—"}</span>
                <span className="text-slate-300">·</span>
                <span>{r.delivery_type === "TRANSFER DELIVERY" ? "Transfer Delivery" : "Regular Delivery"}</span>
              </SheetDescription>
              {reasons.length > 0 && (
                <div><ReasonChips reasons={reasons} /></div>
              )}
              {(rName || oName) && (
                <div data-testid="drawer-store-info" className="flex flex-wrap items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded-sm px-2.5 py-1.5 text-slate-700">
                  <Store size={13} className="text-[#2563EB] shrink-0" />
                  {rName && (<span><span className="text-slate-400">Ruta:</span> <span className="font-semibold">{rName}</span></span>)}
                  {rName && oName && <span className="text-slate-300">·</span>}
                  {oName && (<span><span className="text-slate-400">Origen:</span> <span className="font-semibold">{oName}</span></span>)}
                </div>
              )}
            </SheetHeader>

            {(r.image_url || r.stock_total != null) && (
              <div className="px-5 pt-4" data-testid="drawer-product">
                <div className="flex gap-3 items-start rounded-sm border border-[#E2E8F0] bg-slate-50/60 p-3">
                  <ProductThumb src={r.image_url} className="h-16 w-16" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 leading-tight line-clamp-2">{r.product_description || r.model_number}</p>
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                      {r.model_number}{money(r.product_price) ? ` · ${money(r.product_price)}` : ""}
                    </p>
                    {r.stock_total != null && (
                      <div className="grid grid-cols-3 gap-2 mt-2" data-testid="drawer-stock">
                        <StockMini label="Total" value={r.stock_total} tone="slate" />
                        <StockMini label="Bodega 11" value={r.stock_bodega} tone="orange" />
                        <StockMini label={`Tienda ${r.ship_from_location || ""}`} value={r.stock_store} tone="green" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="px-5 py-4">
              {r.reserva_status && (
                <div className="mb-4 rounded-sm border border-[#E2E8F0] bg-slate-50/60 p-3" data-testid="drawer-reserva">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] uppercase tracking-[0.05em] font-semibold text-slate-500">Estado de reserva</span>
                    <ReservaBadge code={r.reserva_status} />
                  </div>
                  {r.delivery_type !== "TRANSFER DELIVERY" && (
                    (r.linked_transfers && r.linked_transfers.length > 0) ? (
                      <div className="mt-2.5 space-y-1.5">
                        <div className="text-[11px] text-slate-400">Transfers vinculados (desde Bodega 11):</div>
                        {r.linked_transfers.map((t) => {
                          const reserved = Number(t.qty_reserved) >= Number(t.qty_ordered || 1) && Number(t.qty_ordered || 0) > 0;
                          return (
                            <div key={t.order_number} data-testid={`drawer-transfer-${t.order_number}`}
                              className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded-sm px-2.5 py-1.5">
                              <span className="font-mono text-slate-700">{t.order_number} <span className="text-slate-400">· {t.model_number}</span></span>
                              <span className={`font-semibold ${reserved ? "text-[#065F46]" : "text-[#92400E]"}`}>
                                {reserved ? "Reservado en Bodega 11" : "Pendiente (no llega a Bodega 11)"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    ) : r.reserva_status !== "RESERVADO_VENTA" && (
                      <p className="mt-2 text-[11px] text-slate-400">Sin transfer vinculado encontrado en el reporte.</p>
                    )
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                  <FileSpreadsheet size={14} className="text-[#2563EB]" /> Reporte STORIS
                </div>
                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                  <Database size={14} className="text-slate-500" /> UI / Orden Abierta
                </div>
              </div>

              <div className="space-y-3">
                <CompareRow label="Ship-To" s={s.ship_to_name} u={u.ship_to_name} />
                <CompareRow label="Order Type" s={s.order_type} u={u.order_type} />
                <CompareRow label="Fecha de entrega" s={s.delivery_date} u={u.delivery_date} diff={has("DIFERENCIA_FECHA")} />
                <CompareRow label="Estatus"
                  s={s.delivery_status_raw ? `${s.delivery_status_raw} (${dlvyStatusLabel(s.delivery_status_mapped)})` : null}
                  u={u.delivery_status_raw ? `${u.delivery_status_raw} (${dlvyStatusLabel(u.delivery_status_mapped)})` : null}
                  diff={has("DIFERENCIA_ESTATUS")} />
                <CompareRow label="Qty Ordenada" s={s.qty_ordered} u={u.qty_ordered} diff={has("DIFERENCIA_CANTIDAD")} />
                <CompareRow label="Qty Reservada" s={s.qty_reserved} u={u.qty_reserved} />
                <CompareRow label="Qty Back Order" s={s.qty_backorder} u={u.qty_backorder} />
                <CompareRow label="Ruta" s={routeLbl(s.truck_route)} u={routeLbl(u.route)} />
                <CompareRow label="Origen (ship from)" s={locLbl(s.ship_from_location)} u={locLbl(u.ship_from_location)} />
                <CompareRow label="Stock Location" s={locLbl(s.stock_location)} u={locLbl(u.stock_location)} />
                <CompareRow label="Método" s={s.delivery_type} u={u.fulfillment_method} />
              </div>

              {r.match_status === "PENDIENTE_STORIS" && (
                <p className="mt-4 text-xs text-[#92400E] bg-[#FEF3C7] border border-[#FBBF24] rounded-sm px-3 py-2">
                  Esta orden está en el reporte STORIS pero no existe en la vista de órdenes abiertas (UI).
                </p>
              )}
              {r.match_status === "EXISTE_UI_NO_REPORTE" && (
                <p className="mt-4 text-xs text-slate-600 bg-slate-50 border border-[#CBD5E1] rounded-sm px-3 py-2">
                  Esta orden abierta existe en la UI pero no apareció en el reporte STORIS (misma ruta y ventana de fechas).
                </p>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-[#E2E8F0] px-5 py-3 flex items-center justify-between">
              <span className="text-xs text-slate-400 font-mono">
                {r.reviewed ? `Revisado por ${r.reviewed_by || "—"}` : "Sin revisar"}
              </span>
              <Button data-testid="drawer-review-button"
                variant={r.reviewed ? "secondary" : "default"}
                onClick={() => reviewMut.mutate(r.id)}
                className="rounded-sm gap-2 h-9">
                {r.reviewed ? <CheckCircle2 size={15} /> : <Circle size={15} />}
                {r.reviewed ? "Quitar revisión" : "Marcar revisado"}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
